import type { Employee, EmployeePayType } from '../types/employee';
import { resolvePayType } from '../types/employee';

const convertIsRetired = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value === 'true' || value === '1';
  return false;
};

const convertHasFourInsurance = (data: Record<string, unknown>): boolean =>
  data.hasFourInsurance === 1 ||
  data.hasFourInsurance === true ||
  data.has_four_insurance === 1 ||
  data.has_four_insurance === true;

/** Firestore raw → Employee 필드 정규화 (Flutter converter 호환) */
export function mapEmployeeFromFirestore(
  id: string,
  data: Record<string, unknown>,
): Employee {
  const isHourly = Boolean(data.isHourly ?? data.is_hourly ?? false);
  const storedPayType = (data.payType ?? data.pay_type) as string | undefined;
  const payType = resolvePayType(storedPayType, isHourly);
  const dailyRateRaw = data.dailyRate ?? data.daily_rate;
  const dailyRate =
    typeof dailyRateRaw === 'number' ? dailyRateRaw : Number(dailyRateRaw ?? 0) || 0;

  return {
    id,
    ...(data as Omit<Employee, 'id' | 'payType' | 'dailyRate' | 'isHourly' | 'hasFourInsurance' | 'isRetired'>),
    isHourly: payType === 'hourly' ? true : payType === 'monthly' ? false : isHourly,
    payType: payType as EmployeePayType,
    dailyRate,
    account_number: (data.account_number ?? data.accountNumber) as number,
    accountNumber: (data.account_number ?? data.accountNumber) as number | undefined,
    hire_date: (data.hire_date ?? data.hireDate) as Employee['hire_date'],
    hireDate: (data.hire_date ?? data.hireDate) as Employee['hireDate'],
    severance_pay: (data.severance_pay ?? data.severancePay) as Employee['severance_pay'],
    severancePay: (data.severance_pay ?? data.severancePay) as Employee['severancePay'],
    isRetired: convertIsRetired(data.isRetired),
    hasFourInsurance: convertHasFourInsurance(data),
    pensionRate: (data.pensionRate ?? data.pension_rate) as number,
    healthInsuranceRate: (data.healthInsuranceRate ?? data.health_insurance_rate) as number,
    employmentInsuranceRate: (data.employmentInsuranceRate ?? data.employment_insurance_rate) as number,
    industrialAccidentRate: (data.industrialAccidentRate ?? data.industrial_accident_rate) as number,
    createdAt: (data.createdAt as Employee['createdAt']) ?? ({} as Employee['createdAt']),
    updatedAt: (data.updatedAt as Employee['updatedAt']) ?? ({} as Employee['createdAt']),
  };
}

/** React → Firestore 쓰기 정규화 */
export function normalizeEmployeeForFirestore(employeeData: Record<string, unknown>): Record<string, unknown> {
  const isHourly = Boolean(employeeData.isHourly);
  const payType = resolvePayType(
    (employeeData.payType ?? employeeData.pay_type) as string | undefined,
    isHourly,
  );
  const dailyRate = Number(employeeData.dailyRate ?? employeeData.daily_rate ?? 0) || 0;

  return {
    ...employeeData,
    payType,
    dailyRate,
    isHourly: payType === 'hourly',
  };
}
